import { trackError, trackUserEvent } from '@vend/utilities'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS } from '../../constants'
import { SaleManager } from '../../managers/sale-manager'
import {
  PaymentRequestDto,
  RetailerDetails,
} from '../../models/invoicing-payment-request'
import {
  selectSaleId,
  selectSaleLocalId,
  selectQuoteId,
} from '../../state/slices/currentSale/currentSaleSlice'
import { store } from '../../state/store'
import { getFeatureFromCache } from '../../util/feature-cache'
import {
  createPaymentRequest,
  ACCOUNT_NOT_PROVISIONED,
} from './create-invoicing-payment-request'
import { ensureSaleIsSaved } from './ensure-sale-is-saved'
import { validateMerchantAccount } from './validate-merchant-account'

type SaveSaleAndCreatePaymentRequestResponse = {
  success: boolean
  paymentRequestId?: string
  proposalUrl?: string
  requestedAmount?: number
  error?: string
}

/**
 * Unit of work for creating a payment request from the sale payment screen.
 * Enabled for fulfillment sales and non-fulfillment (that can be Layaway) ones.
 * This orchestrates the necessary steps to:
 * 1. Ensure the current sale is saved
 * 2. Create the payment request
 *
 * @param saleManager {SaleManager} The sale manager instance (provides access to $ngRedux)
 * @param paymentRequestData {PaymentRequestDto} The payment request data to send
 * @return {Promise<SaveSaleAndCreatePaymentRequestResponse>} A promise resolved when the operation completes
 */
export const saveSaleAndCreatePaymentRequest = async (
  saleManager: SaleManager,
  paymentRequestData: PaymentRequestDto,
  retailerDetails?: RetailerDetails
): Promise<SaveSaleAndCreatePaymentRequestResponse> => {
  const state = store.getState()
  const saleId = selectSaleId(state)
  const saleLocalId = selectSaleLocalId(state)
  const saleIdentifier = saleId || saleLocalId
  const customerRef = paymentRequestData.customer?.customerRef

  try {
    // Validate feature flag
    if (!getFeatureFromCache(FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS)) {
      return {
        success: false,
        error: 'Invoicing Payment Requests feature is disabled.',
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    // Validate there is sale local ID
    if (!saleLocalId) {
      return {
        success: false,
        error: 'No sale local ID available to create payment request.',
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    trackUserEvent('Web Register', 'Payment Request Flow Started', {
      saleId: saleIdentifier,
      customerRef,
    })

    // Ensure sale is saved before creating payment request
    const saveResult = await ensureSaleIsSaved(saleManager, saleId, saleLocalId)
    if (!saveResult.success) {
      return {
        success: false,
        error: saveResult.error,
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    // Get receipt number details after sale is saved
    // default to saleLocalId if receipt number details are not available
    const updatedState = store.getState()
    const invoiceNumber =
      typeof updatedState.currentSale.receiptNumberDetails === 'string'
        ? updatedState.currentSale.receiptNumberDetails
        : updatedState.currentSale.receiptNumberDetails?.number?.toString() ??
          saleLocalId

    // Use saleLocalId for documentRefs as it's the primary sale identifier in the system
    // and is always available (unlike saleId which is updated async after sale is set as complete)
    if (!paymentRequestData.documentRefs.includes(saleLocalId)) {
      paymentRequestData.documentRefs.push(saleLocalId)
    }

    // Update metadata with the saved sale ID and invoice number
    if (!paymentRequestData.metadata) {
      paymentRequestData.metadata = {}
    }
    paymentRequestData.metadata.documentRef = saleLocalId
    paymentRequestData.metadata.hrOrderId = invoiceNumber

    // If the sale originates from a quote, add the quote ID to documentRefs
    // and set the payment action accordingly.
    // quoteId is populated by _handlePostQuoteSuccess after createQuoteMutation resolves.
    const quoteId = selectQuoteId(store.getState())
    if (quoteId) {
      if (!paymentRequestData.documentRefs.includes(`hr-quote-${quoteId}`)) {
        paymentRequestData.documentRefs.push(`hr-quote-${quoteId}`)
      }
      paymentRequestData.paymentAction = 'quote'
      paymentRequestData.metadata.documentRef = quoteId
      paymentRequestData.pricingTables?.forEach(table => {
        table.title = 'Quote'
      })
    }

    // Create the payment request
    let paymentRequestResult = await createPaymentRequest(paymentRequestData)

    // If account is not provisioned and we have retailer details, provision and retry once
    if (
      !paymentRequestResult.success &&
      paymentRequestResult.errorCode === ACCOUNT_NOT_PROVISIONED &&
      retailerDetails
    ) {
      await validateMerchantAccount(retailerDetails)
      paymentRequestResult = await createPaymentRequest(paymentRequestData)
    }

    if (!paymentRequestResult.success) {
      return {
        success: false,
        error: paymentRequestResult.error,
        paymentRequestId: paymentRequestResult.paymentRequestId,
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    trackUserEvent('Web Register', 'Payment Request Created from Sale Screen', {
      saleId: saleIdentifier,
      paymentRequestId: paymentRequestResult.paymentRequestId,
      customerRef,
    })

    return {
      success: true,
      paymentRequestId: paymentRequestResult.paymentRequestId,
      proposalUrl: paymentRequestResult.proposalUrl,
      requestedAmount: paymentRequestData.requestedAmount,
    }
  } catch (err: any) {
    const errorMessage =
      err?.message ||
      'Unknown error occurred in payment request processing flow.'

    trackError('Payment Request Flow Error', errorMessage, {
      saleId: saleIdentifier,
      customerRef,
    })

    return {
      success: false,
      error: errorMessage,
      requestedAmount: paymentRequestData.requestedAmount,
    }
  }
}
